import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Scissors, Store, CheckCircle, ArrowRight, Star, Shield, Zap, BarChart3, MessageSquare, Settings, CreditCard, Globe, Smartphone } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const FeaturesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('customers');

  const customerFeatures = [
    {
      icon: <Users className="w-8 h-8 text-green-600" />,
      title: "Tailor Discovery",
      description: "Find verified local tailors with detailed profiles, ratings, and portfolios",
      benefits: ["Browse by location", "View tailor portfolios", "Read customer reviews", "Compare pricing"]
    },
    {
      icon: <Shield className="w-8 h-8 text-green-600" />,
      title: "Measurement Vault",
      description: "Secure storage for your body measurements accessible to authorized tailors",
      benefits: ["Encrypted storage", "Easy updates", "Multiple measurement sets", "Privacy controls"]
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-green-600" />,
      title: "Order Tracking",
      description: "Real-time updates on your tailoring orders with detailed progress tracking",
      benefits: ["Live status updates", "Photo progress", "Delivery estimates", "Communication hub"]
    },
    {
      icon: <Smartphone className="w-8 h-8 text-green-600" />,
      title: "Mobile Access",
      description: "Access your measurements and orders from anywhere on any device",
      benefits: ["Responsive design", "Push notifications", "Offline access", "Cross-platform sync"]
    }
  ];

  const tailorFeatures = [
    {
      icon: <Scissors className="w-8 h-8 text-brand-blue" />,
      title: "Business Management",
      description: "Complete CRM system for managing customers, orders, and business operations",
      benefits: ["Customer database", "Order management", "Payment processing", "Business analytics"]
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-brand-blue" />,
      title: "Advanced Analytics",
      description: "Comprehensive insights into your business performance and customer trends",
      benefits: ["Revenue tracking", "Customer insights", "Performance metrics", "Growth forecasting"]
    },
    {
      icon: <Settings className="w-8 h-8 text-brand-blue" />,
      title: "Staff Management",
      description: "Manage your team with role-based access and performance tracking",
      benefits: ["Role assignments", "Permission control", "Performance tracking", "Team collaboration"]
    },
    {
      icon: <CreditCard className="w-8 h-8 text-brand-blue" />,
      title: "Payment Integration",
      description: "Seamless payment processing with multiple payment methods and invoicing",
      benefits: ["Multiple payment methods", "Automated invoicing", "Payment tracking", "Financial reporting"]
    }
  ];

  const fashionHouseFeatures = [
    {
      icon: <Store className="w-8 h-8 text-brand-yellow" />,
      title: "Inventory Management",
      description: "Comprehensive inventory tracking for fabrics, supplies, and finished products",
      benefits: ["Stock tracking", "Reorder alerts", "Supplier management", "Inventory analytics"]
    },
    {
      icon: <Globe className="w-8 h-8 text-brand-yellow" />,
      title: "Multi-location Support",
      description: "Manage multiple boutiques and locations from a single dashboard",
      benefits: ["Centralized control", "Location-specific data", "Cross-location transfers", "Unified reporting"]
    },
    {
      icon: <Zap className="w-8 h-8 text-brand-yellow" />,
      title: "Bulk Order Processing",
      description: "Efficiently handle large orders and wholesale operations",
      benefits: ["Bulk pricing", "Order templates", "Production scheduling", "Quality control"]
    },
    {
      icon: <Star className="w-8 h-8 text-brand-yellow" />,
      title: "Supplier Collaboration",
      description: "Connect with suppliers and manage relationships seamlessly",
      benefits: ["Supplier directory", "Order management", "Communication tools", "Performance tracking"]
    }
  ];

  const getFeatures = () => {
    switch (activeTab) {
      case 'customers':
        return customerFeatures;
      case 'tailors':
        return tailorFeatures;
      case 'fashion-houses':
        return fashionHouseFeatures;
      default:
        return customerFeatures;
    }
  };

  const getTabColor = () => {
    switch (activeTab) {
      case 'customers':
        return 'border-green-500 text-green-600';
      case 'tailors':
        return 'border-brand-blue text-brand-blue';
      case 'fashion-houses':
        return 'border-brand-yellow text-brand-yellow';
      default:
        return 'border-green-500 text-green-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Master Ji <span className="text-brand-blue">Features</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for custom tailoring success
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center mb-12">
          <div className="bg-white p-2 rounded-xl shadow-lg border border-gray-200">
            <div className="flex space-x-2">
              {[
                { id: 'customers', label: 'Customers', icon: <Users className="w-5 h-5" /> },
                { id: 'tailors', label: 'Tailors', icon: <Scissors className="w-5 h-5" /> },
                { id: 'fashion-houses', label: 'Fashion Houses', icon: <Store className="w-5 h-5" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? tab.id === 'customers'
                        ? 'bg-green-500 text-white shadow-lg scale-105'
                        : tab.id === 'tailors'
                        ? 'bg-brand-blue text-white shadow-lg scale-105'
                        : 'bg-brand-yellow text-white shadow-lg scale-105'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {getFeatures().map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Key Benefits:</h4>
                {feature.benefits.map((benefit, benefitIndex) => (
                  <div key={benefitIndex} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl mx-auto"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Experience These Features?
            </h2>
            <p className="text-gray-600 mb-6">
              Join thousands of users who are already transforming their business with our platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/register'}
                className="bg-brand-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-blue/90 transition-colors flex items-center justify-center space-x-2"
              >
                <span>Get Started Free</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => window.location.href = '/demo'}
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Watch Demo
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FeaturesPage;

